import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { dsDtos, webDtos } from '@vectronic/openApi-admin';
import { webDtos as customerWebDtos, webEnums } from '@vectronic/openApi-customer';
import { DialogService, getCurrentTimespan, latestBuild, Translator } from '@vectronic/util';
import { DetailsItemComponent } from '../../details/details-item/details-item.component';
import { BeltDialogComponent } from '../../modals/belt-dialog/belt-dialog.component';

@Component({
  selector: 'vectronic-collar-specs',
  templateUrl: './collar-specs.component.html',
  styleUrls: ['./collar-specs.component.scss'],
  imports: [DetailsItemComponent, MatButtonModule, MatIconModule, MatTooltipModule, CommonModule],
})
export class CollarSpecsComponent {
  collar = input<customerWebDtos.CollarExtended | webDtos.Collar>();
  lastPosition = input<webDtos.GPSPosition>();
  deviceTypes = input<dsDtos.DeviceType[]>();
  firmwareVersion = input<string | undefined>();
  batteryType = input<string | undefined>();
  pcbType = input<string | undefined>();
  bootloaderVersion = input<string | undefined>();
  beaconId = input<string>();
  isInternalDropOffButtonClickable = input<boolean>();
  isExternalDropOffButtonClickable = input<boolean>();

  showDropOff = output<{ dropOffId: number; collarId: number; type: 'internal' | 'external' }>();

  private dialogService = inject(DialogService);

  adminCollar = computed(
    () =>
      this.collar() as webDtos.Collar & {
        deviceMetaInformation: webDtos.DeviceMetaInformation;
        camera: webDtos.Camera;
      },
  );

  extendedCollar = computed(() => this.collar() as webDtos.CollarExtended);

  build = computed(() => latestBuild(this.adminCollar()));

  vaginalImplantCount = computed(
    () =>
      this.collar()?.parameterDataList?.find(({ parameterId }) => parameterId === 'vitId')?.data,
  );

  mortalityImplantCount = computed(
    () =>
      this.collar()?.parameterDataList?.find(({ parameterId }) => parameterId === 'mitId')?.data,
  );

  cameraId = computed(
    () =>
      this.collar()?.parameterDataList?.find(({ parameterId }) => parameterId === 'cameraId')?.data,
  );

  collarLastPositionAltitude = computed(() => this.lastPosition()?.altitude);

  collarLastPositionTemperature = computed(() => this.lastPosition()?.temperature);

  collarLastPositionBackupVoltage = computed(
    () => this.extendedCollar().lastPositionBackupVoltage ?? this.lastPosition()?.backupVoltage,
  );

  collarLastPositionMainVoltage = computed(
    () => this.extendedCollar().lastPositionMainVoltage ?? this.lastPosition()?.mainVoltage,
  );

  beaconFrequency = computed(() => this.extendedCollar().beaconFrequency);

  shortrange = computed(() => {
    if (this.build()?.uhfCommunication) return 'UHF';
    return 'None';
  });

  beaconParameter = computed(
    () =>
      this.adminCollar().parameterDataList?.find(
        ({ parameterId }) => parameterId === this.beaconId(),
      )?.data,
  );

  belt = computed(() => {
    const belt = this.build()?.belt;
    if (belt?.length) return `${belt.length || ''} - ${belt.shape || ''}`;
  });

  isBeltEmpty = computed(() => {
    const belt = this.build()?.belt;
    return !belt || !Object.entries(belt).length;
  });

  deviceTypeName = computed(() => {
    const deviceType = this.deviceTypes()?.find(
      (deviceType) => deviceType.id === this.adminCollar().deviceTypeId,
    );
    return deviceType?.customerName;
  });

  translateComType(comType: webEnums.ComType) {
    return Translator.comType(comType);
  }

  formatNumber(number: string) {
    if (!number) return '';
    return (parseInt(number) / 1000000).toFixed(3).replace(/\.(\d)$/, '.$10');
  }

  onClickBelt() {
    this.dialogService.open(BeltDialogComponent, {
      data: {
        belt: this.build()?.belt,
        idDevice: this.adminCollar().idDevice,
      },
    });
  }

  onClickDropOffExternal() {
    this.showDropOff.emit({
      dropOffId: this.adminCollar().externalDropOffId,
      collarId: this.adminCollar().idDevice,
      type: 'external',
    });
  }

  onClickDropOffInternal() {
    this.showDropOff.emit({
      dropOffId: this.adminCollar().internalDropOffId,
      collarId: this.adminCollar().idDevice,
      type: 'internal',
    });
  }

  getDropOffButtonState(isClickable: boolean) {
    return {
      disabled: !isClickable,
      tooltip: isClickable ? 'Show Drop Off info' : 'No Drop Off permissions',
      iconColor: isClickable ? 'accent' : '',
      cursorState: !isClickable ? 'disabled-cursor' : '',
    };
  }

  protected readonly getCurrentTimespan = getCurrentTimespan;
}
